import React from 'react';

function DayView({ currentDate, events, handleEvent }) {
	const dayEvents = events.filter((event) => {
		const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
		return eventDates.includes(currentDate.toDateString());
	});

	return (
		<div className="day-view">
			<div className="day-header d-flex flex-column justify-content-start align-items-start">
				<div className="day-number gray_dark_01 weight-500">
					{currentDate.getDate().toString().padStart(2, '0')}
				</div>
				<div className="day-subtitle regular-14 gray_dark_01">
					{currentDate.toLocaleDateString('en-US', { month: 'short', weekday: 'long' })}
				</div>
			</div>

			<div className="day-events">
				<table className="table with-border no-hover">
					<tbody>
						{dayEvents?.map((event) => (
							<tr key={`event_${event.id}`} onClick={() => handleEvent(event.id)}>
								<td className="p-0">
									<div className={`single-day-event cursor-pointer ${event?.color}`}>
										<div className={`event-single-content p-1`}>
											<p className="medium-12 gray_dark_01 mb-1">{event?.title}</p>
											<p className="regular-12 gray_dark_02 mb-1">
												{event?.projectType}
											</p>
											<p className="regular-12 gray_dark_02 mb-1">{event?.brand}</p>
										</div>
									</div>
								</td>
							</tr>
						))}

						<tr>
							<td></td>
						</tr>
						<tr>
							<td></td>
						</tr>
						<tr>
							<td></td>
						</tr>
					</tbody>
				</table>
			</div>
		</div>
	);
}

function WeekView({ currentDate, events, handleEvent }) {
	const startOfWeek = new Date(currentDate);
	const day = startOfWeek.getDay();
	const diff = startOfWeek.getDate() - day + (day === 0 ? -6 : 1);
	startOfWeek.setDate(diff);

	const getEventsForDate = (date) => {
		return events.filter((event) => {
			const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
			return eventDates.includes(date.toDateString());
		});
	};

	const weekDays = [];
	for (let i = 0; i < 7; i++) {
		const date = new Date(startOfWeek);
		date.setDate(startOfWeek.getDate() + i);
		weekDays.push({ date, events: getEventsForDate(date) });
	}

	const dayNames = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

	const getMaxEventLength = (data) => {
		if (!Array.isArray(data)) return 0;

		return data.reduce((max, day) => {
			const length = Array.isArray(day.events) ? day.events.length : 0;
			return Math.max(max, length);
		}, 0);
	};

	return (
		<>
			<div className="d-flex flex-column">
				<div className="week-view scroll-x-label">
					<div className="week-header">
						{weekDays.map((item, index) => {
							const isToday = new Date().toDateString() === item.date.toDateString();
							return (
								<div key={index} className={`week-day-header p-1 ${isToday ? 'today' : ''}`}>
									<div className="week-day-number semibold-16 gray_dark_01">
										{item.date.getDate().toString().padStart(2, '0')}
									</div>
									<div className="week-day-label regular-12 gray_dark_02">
										{dayNames[index].substring(0, 3)}
									</div>
								</div>
							);
						})}
					</div>

					<div className="week-grid">
						{Array.from({ length: getMaxEventLength(weekDays) + 3 }).map((_, eventIndex) => {
							return weekDays.map((item, index) => {
								const isToday = new Date().toDateString() === item.date.toDateString();
								const event = item.events[eventIndex] || {};
								return (
									<div
										key={index}
										className={`week-cell ${event.color} week-event-cell ${isToday ? 'today-cell' : ''}`}
									>
										<div
											className={`week-cell-content p-2 mb-2 cursor-pointer`}
											key={`event_${event.id}`}
											onClick={() => handleEvent(event.id)}
										>
											<p className="medium-14 gray_dark_01">{event?.title}</p>
											<p className="regular-12 gray_dark_02">{event?.projectType}</p>
											<p className="regular-12 gray_dark_02">{event?.brand}</p>
										</div>
									</div>
								);
							});
						})}
					</div>
				</div>
				<div className="all-events-box">
					<p className="semibold-14 gray_dark_01 py-3">Project type & Color</p>
					<div className="events-items d-flex align-items-center flex-wrap justify-content-start">
						<div className="event-single">
							<div className="event-single-content event-blue"></div>
							<p className="regular-14 gray_dark_01 title">Buyer Sign off</p>
						</div>
						<div className="event-single">
							<div className="event-single-content event-yellow"></div>
							<p className="regular-14 gray_dark_01 title">Buyer visit</p>
						</div>
						<div className="event-single">
							<div className="event-single-content event-green"></div>
							<p className="regular-14 gray_dark_01 title">Others project type</p>
						</div>
						<div className="event-single">
							<div className="event-single-content event-gray"></div>
							<p className="regular-14 gray_dark_01 title">If project is completed</p>
						</div>
					</div>
				</div>
			</div>
		</>
	);
}

function MonthView({ currentDate, events, handleEvent }) {
	const year = currentDate.getFullYear();
	const month = currentDate.getMonth();
	const firstDay = new Date(year, month, 1);
	const lastDay = new Date(year, month + 1, 0);
	const daysInMonth = lastDay.getDate();
	const startingDayOfWeek = firstDay.getDay();

	const days = [];
	// 1. Previous month's trailing days
	const prevMonthLastDay = new Date(year, month, 0).getDate();
	for (let i = startingDayOfWeek - 1; i >= 0; i--) {
		const day = prevMonthLastDay - i;
		const date = new Date(year, month - 1, day);

		const dayEvents = events.filter((event) => {
			const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
			return eventDates.includes(date.toDateString());
		});

		days.push({ day, date, events: dayEvents, isOtherMonth: true });
	}

	// 2. Current month days
	for (let day = 1; day <= daysInMonth; day++) {
		const date = new Date(year, month, day);

		const dayEvents = events.filter((event) => {
			const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
			return eventDates.includes(date.toDateString());
		});

		days.push({ day, date, events: dayEvents, isOtherMonth: false });
	}

	// 3. Fill next month's leading days to complete 42 cells
	let nextDay = 1;
	while (days.length < 42) {
		const date = new Date(year, month + 1, nextDay);

		const dayEvents = events.filter((event) => {
			const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
			return eventDates.includes(date.toDateString());
		});

		days.push({ day: nextDay, date, events: dayEvents, isOtherMonth: true });
		nextDay++;
	}

	return (
		<div className="month-view">
			<div className="month-header-grid">
				{['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'].map((d, i) => (
					<div key={i} className="month-header-cell text-start regular-14 weight-500">
						{d}
					</div>
				))}
			</div>

			<div className="month-grid">
				{days.map((dayData, index) => {
					if (!dayData) return <div key={index} className="month-empty-cell"></div>;
					const isToday = new Date().toDateString() === dayData.date.toDateString();
					return (
						<div
							key={index}
							className={`month-day ${isToday ? 'month-event-day' : ''} ${dayData?.isOtherMonth ? 'disable-cell with-prev-month' : ''}`}
						>
							<div className="month-day-number semibold-16 mb-2">{dayData.day}</div>
							{dayData.events?.map((item) => (
								<div
									className={`month-event-day-content mb-2`}
									key={`content_${item.id}`}
									onClick={() => handleEvent(item.id)}
								>
									<div
										className={`regular-12 gray_dark_01 p-2 cursor-pointer ${item.color}`}
									>
										<p className="medium-14">{item?.title}</p>
										<p>{[item?.projectType, item?.brand].filter(Boolean).join(', ')}</p>
									</div>
								</div>
							))}
						</div>
					);
				})}
			</div>
		</div>
	);
}

function YearView({ currentDate, events, handleSelectedDate }) {
	const year = currentDate.getFullYear();
	const months = [
		'January',
		'February',
		'March',
		'April',
		'May',
		'June',
		'July',
		'August',
		'September',
		'October',
		'November',
		'December'
	];

	const getMonthCalendar = (monthIndex) => {
		const firstDay = new Date(year, monthIndex, 1);
		const lastDay = new Date(year, monthIndex + 1, 0);
		const daysInMonth = lastDay.getDate();
		const startingDayOfWeek = firstDay.getDay();

		const days = [];

		// 1. Add previous month's trailing days
		const prevMonthLastDay = new Date(year, monthIndex, 0).getDate();
		for (let i = startingDayOfWeek - 1; i >= 0; i--) {
			const day = prevMonthLastDay - i;
			const date = new Date(year, monthIndex - 1, day);
			const hasEvents = events.some((event) => {
				const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
				return eventDates.includes(date.toDateString());
			});
			days.push({
				day,
				date,
				hasEvents,
				isToday: new Date().toDateString() === date.toDateString(),
				isOtherMonth: true
			});
		}

		// 2. Current month days
		for (let day = 1; day <= daysInMonth; day++) {
			const date = new Date(year, monthIndex, day);
			let projectId = null;
			const hasEvents = events.some((event) => {
				projectId = event.id;
				const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
				return eventDates.includes(date.toDateString());
			});
			days.push({
				day,
				date,
				hasEvents,
				isToday: new Date().toDateString() === date.toDateString(),
				isOtherMonth: false,
				projectId
			});
		}

		// 3. Fill next month to complete 42 cells (6 rows)
		let nextDay = 1;
		while (days.length < 42) {
			const date = new Date(year, monthIndex + 1, nextDay);
			const hasEvents = events.some((event) => {
				const eventDates = event.milestoneDates.map((d) => new Date(d).toDateString());
				return eventDates.includes(date.toDateString());
			});
			days.push({
				day: nextDay,
				date,
				hasEvents,
				isToday: new Date().toDateString() === date.toDateString(),
				isOtherMonth: true
			});
			nextDay++;
		}

		return days;
	};

	return (
		<div className="year-view bg-white">
			<div className="year-grid">
				{months.map((monthName, monthIndex) => {
					const monthDays = getMonthCalendar(monthIndex);
					return (
						<div key={monthIndex} className="month-card">
							<div className="month-header red regular-14 weight-500 text-white">
								{monthName}
							</div>
							<div className="month-weekdays">
								{['S', 'M', 'T', 'W', 'T', 'F', 'S'].map((day, i) => (
									<div key={i} className="weekday-cell">
										{day}
									</div>
								))}
							</div>
							<div className="month-days">
								{monthDays.map((dayData, dayIndex) => (
									<div
										key={dayIndex}
										className={`month-day-cell cursor-pointer ${dayData?.hasEvents ? 'has-event project-date-cell d-flex justify-content-center align-items-center' : ''} ${dayData?.isOtherMonth ? 'disable-cell' : ''} ${dayData?.isToday ? 'today-day d-flex justify-content-center align-items-center' : ''} `}
										onClick={() => {
											if (!dayData?.isOtherMonth) {
												// handleEvent(dayData.projectId);
												handleSelectedDate(dayData.date);
											}
										}}
									>
										{dayData ? dayData.day : ''}
									</div>
								))}
							</div>
						</div>
					);
				})}
			</div>
		</div>
	);
}

export function CalendarGrid({ currentDate, events, viewMode, handleEvent, handleSelectedDate }) {
	if (viewMode === 'Year')
		return (
			<YearView
				currentDate={currentDate}
				events={events}
				handleEvent={handleEvent}
				handleSelectedDate={handleSelectedDate}
			/>
		);
	if (viewMode === 'Month')
		return <MonthView currentDate={currentDate} events={events} handleEvent={handleEvent} />;
	if (viewMode === 'Week')
		return <WeekView currentDate={currentDate} events={events} handleEvent={handleEvent} />;
	return <DayView currentDate={currentDate} events={events} handleEvent={handleEvent} />;
}
